import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Material-UI components
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Stack,
  styled,
  AppBar,
  Toolbar,
  Drawer,
  Divider,
  IconButton
} from '@mui/material';
// Material-UI icons
import {
  ArrowForward,
  PlayArrow,
  Dashboard,
  VerifiedUser,
  Shield,
  Security,
  CheckCircle,
  BarChart,
  KeyboardArrowUp,
  People,
  Psychology,
  Payment,
  BusinessCenter,
  Apartment,
  Store,
  School,
  VolunteerActivism,
  LocationCity,
  Language as LanguageIcon,
  PieChart,
  Insights,
  Assessment,
  ShowChart,
  CheckCircleOutline,
  Menu,
  Close,
  Lock,
  DataArray,
  Storage,
  PrivacyTip,
  Https,
  Article,
  FileUpload
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Typewriter from 'typewriter-effect';

// Data structures for the landing page
// Features data
const features = [
  {
    id: 1,
    name: 'Member Management',
    description: 'Easily manage your entire member database with powerful segmentation tools and automated workflows.',
    icon: <People sx={{ fontSize: 40, color: '#1565c0' }} />,
    color: '#bbdefb',
    gradient: 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
  },
  {
    id: 2,
    name: 'Custom Experiences',
    description: 'Create fully branded, personalized portals for your members, clients, or customers with intuitive drag-and-drop tools.',
    icon: <Psychology sx={{ fontSize: 40, color: '#7c4dff' }} />,
    color: '#e1bee7',
    gradient: 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)'
  },
  {
    id: 3,
    name: 'Payment Tracking',
    description: 'Track membership payments, dues, and generate detailed financial reports with automated reconciliation.',
    icon: <Payment sx={{ fontSize: 40, color: '#43a047' }} />,
    color: '#c8e6c9',
    gradient: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)'
  },
  {
    id: 4,
    name: 'Member Portal',
    description: 'Provide your members with a personalized portal to view their membership status, benefits, and exclusive content.',
    icon: <Dashboard sx={{ fontSize: 40, color: '#ff5722' }} />,
    color: '#ffccbc',
    gradient: 'linear-gradient(135deg, #ffccbc 0%, #ffab91 100%)'
  },
  {
    id: 5,
    name: 'Customizable Branding',
    description: 'Add your logo, colors, and branding to create a seamless experience that matches your organization identity.',
    icon: <BusinessCenter sx={{ fontSize: 40, color: '#f9a825' }} />,
    color: '#fff9c4',
    gradient: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)'
  },
  {
    id: 6,
    name: 'Multi-Tenant Architecture',
    description: 'Each organization gets its own secure space with dedicated data isolation and customization options.',
    icon: <Apartment sx={{ fontSize: 40, color: '#00acc1' }} />,
    color: '#b2ebf2',
    gradient: 'linear-gradient(135deg, #b2ebf2 0%, #80deea 100%)'
  }
];

// Security features data
const securityFeatures = [
  {
    id: 1,
    name: 'End-to-End Encryption',
    description: 'All sensitive data is encrypted in transit and at rest using industry-standard AES-256 encryption with zero-knowledge architecture.',
    icon: <Lock sx={{ fontSize: 40, color: '#1565c0' }} />,
    color: '#bbdefb',
    gradient: 'linear-gradient(135deg, #bbdefb 0%, #90caf9 100%)'
  },
  {
    id: 2,
    name: 'GDPR & CCPA Compliant',
    description: 'Our platform is fully compliant with global privacy regulations including GDPR, CCPA, and HIPAA for healthcare organizations.',
    icon: <PrivacyTip sx={{ fontSize: 40, color: '#7c4dff' }} />,
    color: '#e1bee7',
    gradient: 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)'
  },
  {
    id: 3,
    name: 'Multi-factor Authentication',
    description: 'Secure your account with SMS, email, authenticator apps, and hardware security key options for multi-factor authentication.',
    icon: <Https sx={{ fontSize: 40, color: '#43a047' }} />,
    color: '#c8e6c9',
    gradient: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)'
  },
  {
    id: 4,
    name: 'Data Isolation',
    description: 'Each tenant\'s data is strictly isolated in dedicated database schemas with comprehensive access controls and audit logging.',
    icon: <Storage sx={{ fontSize: 40, color: '#ff5722' }} />,
    color: '#ffccbc',
    gradient: 'linear-gradient(135deg, #ffccbc 0%, #ffab91 100%)'
  },
  {
    id: 5,
    name: 'Regular Security Audits',
    description: 'Our platform undergoes quarterly penetration testing by third-party security firms with SOC 2 Type II certification.',
    icon: <Shield sx={{ fontSize: 40, color: '#f9a825' }} />,
    color: '#fff9c4',
    gradient: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)'
  },
  {
    id: 6,
    name: 'Compliance Documentation',
    description: 'Access ready-to-use compliance documentation to meet your regulatory requirements and simplify your audit processes.',
    icon: <Article sx={{ fontSize: 40, color: '#00acc1' }} />,
    color: '#b2ebf2',
    gradient: 'linear-gradient(135deg, #b2ebf2 0%, #80deea 100%)'
  }
];

// Sample analytics metrics to showcase
const analyticsMetrics = [
  {
    name: 'Member Engagement',
    description: 'Track member activity, content interactions, and attendance trends over time.',
    icon: <Insights sx={{ fontSize: 40, color: '#fff' }} />,
    color: '#3f51b5',
    gradient: 'linear-gradient(135deg, #3949ab 0%, #5c6bc0 100%)',
    stat: '37% increase',
    chartType: 'line'
  },
  {
    name: 'Revenue Analytics',
    description: 'Monitor payment trends, subscription renewals, and financial forecasting.',
    icon: <ShowChart sx={{ fontSize: 40, color: '#fff' }} />,
    color: '#2e7d32',
    gradient: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
    stat: '$142K in Q2',
    chartType: 'bar'
  },
  {
    name: 'Usage Patterns',
    description: 'Understand how members interact with your portal and which features drive value.',
    icon: <PieChart sx={{ fontSize: 40, color: '#fff' }} />,
    color: '#d32f2f',
    gradient: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)',
    stat: '12 key insights',
    chartType: 'pie'
  },
  {
    name: 'Retention Metrics',
    description: 'Visualize member retention rates and identify factors affecting churn.',
    icon: <Assessment sx={{ fontSize: 40, color: '#fff' }} />,
    color: '#f57c00',
    gradient: 'linear-gradient(135deg, #ef6c00 0%, #ffa726 100%)',
    stat: '93% retention',
    chartType: 'area'
  }
];

// Testimonials
const testimonials = [
  {
    content: "This membership portal has transformed how we manage our club members. The custom fields option is a game changer for us.",
    author: "Sarah Johnson",
    role: "Club President",
    org: "Valley Sports Club"
  },
  {
    content: "We've reduced administration time by 70% since implementing this system. Our members love the self-service options too.",
    author: "Michael Chen",
    role: "Membership Director",
    org: "Downtown Professional Network"
  },
  {
    content: "From registration to payment tracking, everything is streamlined. It's exactly what our organization needed.",
    author: "Lisa Rodriguez",
    role: "Executive Director",
    org: "Community Arts Association"
  }
];

// Custom styled components for enhanced enterprise appearance
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
  color: 'white',
  fontWeight: 600,
  padding: '10px 20px',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.12)'
  }
}));

const EnhancedHeroSection = () => {
  return (
    <Box
      id="home"
      sx={{
        background: 'linear-gradient(135deg, #0a1929 0%, #1a2942 100%)',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 6, md: 8 },
        pb: { xs: 8, md: 10 },
        minHeight: { xs: 'auto', md: '85vh' },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ pr: { md: 4 } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  color="white"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    background: 'linear-gradient(90deg, #ffffff 0%, #bbdefb 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2,
                  }}
                >
                  Streamline Your Membership Management
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    lineHeight: 1.5,
                  }}
                >
                  All-in-one platform for managing members, subscriptions, and payments with powerful analytics.
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 3,
                      background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(90deg, #1976d2, #7b1fa2)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      },
                      transition: 'all 0.3s',
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                    }}
                    onClick={() => window.location.href = '/register-tenant'}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    component="a"
                    href="#features"
                    variant="outlined"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const EnhancedFeaturesSection = () => {
  return (
    <Box id="features" sx={{ py: 10, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Key Features
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: 'linear-gradient(90deg, #1976d2 30%, #9c27b0 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Enterprise-Grade Membership Platform
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            A comprehensive solution designed to meet the needs of organizations of all sizes
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.id}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <FeatureCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        borderRadius: '16px',
                        p: 1.5,
                        mb: 2,
                        background: feature.gradient
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                      {feature.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const SecuritySection = () => {
  return (
    <Box id="security" sx={{ py: 10, backgroundColor: '#f5f7fa' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Enterprise Security
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: 'linear-gradient(90deg, #0d47a1 30%, #1a237e 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Bank-Grade Security & Compliance
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Your data security and privacy are our top priorities - backed by industry certifications and global compliance
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {securityFeatures.map((feature) => (
            <Grid item xs={12} md={4} key={feature.id}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <FeatureCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        borderRadius: '16px',
                        p: 1.5,
                        mb: 2,
                        background: feature.gradient
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                      {feature.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const EnhancedAnalyticsDashboard = () => {
  return (
    <Box id="analytics" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            DATA-DRIVEN INSIGHTS
          </Typography>
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="bold" 
            gutterBottom
            sx={{ 
              background: 'linear-gradient(90deg, #2e7d32 30%, #00796b 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Enterprise Analytics Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Transform membership data into actionable insights with our powerful analytics platform
          </Typography>
        </Box>

        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <Paper 
            elevation={4}
            sx={{ 
              borderRadius: '20px', 
              overflow: 'hidden',
              position: 'relative',
              mb: 6
            }}
          >
            <Box
              sx={{ 
                height: { xs: 400, sm: 500, md: 600 }, 
                width: '100%', 
                position: 'relative',
                background: 'linear-gradient(135deg, #0a1929 0%, #102a43 100%)',
              }}
            >
              <Box
                component="img"
                src="https://source.unsplash.com/random/1800x900?dashboard+analytics"
                alt="Analytics Dashboard"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: 0.7
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  p: { xs: 3, md: 5 }
                }}
              >
                <Box sx={{ maxWidth: '600px' }}>
                  <Typography 
                    variant="h4" 
                    color="white" 
                    fontWeight="bold" 
                    sx={{ mb: 2, textShadow: '0px 2px 4px rgba(0,0,0,0.3)' }}
                  >
                    Real-time insights at your fingertips
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    color="rgba(255,255,255,0.85)" 
                    sx={{ mb: 3 }}
                  >
                    Visualize member engagement, track revenue trends, and identify growth opportunities with our powerful analytics suite.
                  </Typography>
                </Box>
              </Box>

              {/* Floating analytics elements for visual appeal */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                viewport={{ once: true }}
                sx={{
                  position: 'absolute',
                  top: '10%',
                  right: '5%',
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  borderRadius: '10px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  width: { xs: '200px', md: '250px' },
                  display: { xs: 'none', md: 'block' }
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Member Growth Trend
                </Typography>
                <Box sx={{ height: '80px', display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  {[30, 45, 35, 60, 75, 50, 85, 70, 90].map((height, i) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        height: `${height}%`, 
                        width: '9%', 
                        bgcolor: i === 8 ? '#2e7d32' : '#bbdefb',
                        '&:hover': { bgcolor: '#2e7d32' },
                        transition: 'background-color 0.3s'
                      }} 
                    />
                  ))}
                </Box>
              </Box>

              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                viewport={{ once: true }}
                sx={{
                  position: 'absolute',
                  bottom: '15%',
                  left: '10%',
                  p: 2,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  borderRadius: '10px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                  width: { xs: '150px', md: '200px' },
                  display: { xs: 'none', md: 'block' }
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Revenue Metrics
                </Typography>
                <Box sx={{ position: 'relative', height: '80px' }}>
                  <Box sx={{ 
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '70%',
                    background: 'linear-gradient(to top, rgba(46,125,50,0.3) 0%, transparent 100%)',
                    clipPath: 'polygon(0 100%, 10% 70%, 20% 85%, 30% 60%, 40% 40%, 50% 50%, 60% 45%, 70% 30%, 80% 40%, 90% 20%, 100% 40%, 100% 100%)'
                  }}/>
                  <Box sx={{ 
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    borderTop: '2px solid #2e7d32',
                    clipPath: 'polygon(0 0, 10% 30%, 20% 15%, 30% 40%, 40% 60%, 50% 50%, 60% 55%, 70% 70%, 80% 60%, 90% 80%, 100% 60%)'
                  }}/>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Dashboard stats with nice visual treatment */}
        <Grid container spacing={3}>
          {analyticsMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      backgroundImage: metric.gradient,
                      color: 'white'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {metric.name}
                      </Typography>
                      <Avatar
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          height: 40, 
                          width: 40
                        }}
                      >
                        {metric.icon}
                      </Avatar>
                    </Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 2 }}>
                      {metric.stat}
                    </Typography>
                  </Box>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {metric.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const TestimonialsSection = () => {
  return (
    <Box id="testimonials" sx={{ py: 10, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            gutterBottom
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Customer Success Stories
          </Typography>
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="bold" 
            gutterBottom
            sx={{ 
              background: 'linear-gradient(90deg, #9c27b0 30%, #673ab7 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            What Our Customers Say
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Organizations of all sizes trust our platform to manage their membership operations
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    p: 3,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 32px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <Box sx={{ mb: 3, display: 'flex' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Box 
                        key={star}
                        component="span" 
                        sx={{ 
                          color: '#FFC107',
                          fontSize: '1.5rem',
                          lineHeight: 1
                        }}
                      >
                        ★
                      </Box>
                    ))}
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      fontSize: '1.05rem',
                      mb: 3,
                      flex: 1,
                      color: 'text.primary'
                    }}
                  >
                    "{testimonial.content}"
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: index === 0 ? '#673ab7' : index === 1 ? '#2196f3' : '#ff5722',
                        width: 56,
                        height: 56,
                        mr: 2
                      }}
                    >
                      {testimonial.author.split(' ').map(word => word[0]).join('')}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {testimonial.author}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}, {testimonial.org}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
        
        {/* Customer logos section */}
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            sx={{ mb: 6 }}
          >
            Trusted by organizations worldwide
          </Typography>
          
          <Grid 
            container 
            spacing={4} 
            justifyContent="center"
            sx={{
              opacity: 0.8,
              '& > *': {
                filter: 'grayscale(100%)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  filter: 'grayscale(0%)',
                  opacity: 1,
                  transform: 'scale(1.05)'
                }
              }
            }}
          >
            {['Accor Inc', 'TechCorp', 'GlobalServe', 'Innovex', 'DataFlow', 'MetroOrg'].map((company, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <Box 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {company}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

const NavigationMenu = () => {
  const [navbarBackground, setNavbarBackground] = useState('rgba(10, 25, 41, 0.95)');
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setNavbarBackground('rgba(10, 25, 41, 0.95)');
      } else {
        setNavbarBackground('rgba(10, 25, 41, 0.95)');
      }

      const sections = ['home', 'features', 'security', 'analytics', 'testimonials', 'contact'];
      let currentActive = 'home';

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentActive = section;
            break;
          }
        }
      }

      setActiveSection(currentActive);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', link: '#home' },
    { name: 'Features', link: '#features' },
    { name: 'Security', link: '#security' },
    { name: 'Analytics', link: '#analytics' },
    { name: 'Testimonials', link: '#testimonials' },
    { name: 'Marketing Materials', link: '/marketing-materials' },
    { name: 'Contact', link: '#contact' },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: navbarBackground,
        transition: 'background-color 0.3s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
          <Box
            component="a"
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'white',
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="Company Logo"
              sx={{
                height: 35,
                mr: 1,
                display: { xs: 'none', sm: 'block' }
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                background: 'linear-gradient(90deg, #ffffff 30%, #90caf9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              MembershipSuite
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center'
            }}
          >
            {navItems.map((item) => (
              <Button
                key={item.name}
                component="a"
                href={item.link}
                sx={{
                  color: 'white',
                  px: 2,
                  py: 1,
                  position: 'relative',
                  opacity: activeSection === item.link.substring(1) ? 1 : 0.8,
                  fontWeight: activeSection === item.link.substring(1) ? 500 : 400,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '0',
                    left: activeSection === item.link.substring(1) ? '10%' : '50%',
                    width: activeSection === item.link.substring(1) ? '80%' : '0%',
                    height: '2px',
                    backgroundColor: '#64b5f6',
                    transition: 'all 0.3s ease',
                  },
                  '&:hover': {
                    background: 'transparent',
                    opacity: 1,
                    '&::after': {
                      width: '80%',
                      left: '10%'
                    }
                  },
                }}
              >
                {item.name}
              </Button>
            ))}

            <Box sx={{ ml: 2 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  mr: 1,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
                component={Link}
                to="/login"
              >
                Log In
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1976d2, #7b1fa2)',
                  },
                }}
                component={Link}
                to="/register-tenant"
              >
                Sign Up
              </Button>
            </Box>
          </Stack>

          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Button
              color="inherit"
              onClick={toggleMobileMenu}
              endIcon={mobileMenuOpen ? <Close /> : <Menu />}
              sx={{ color: 'white' }}
            >
              Menu
            </Button>
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: '70%',
            maxWidth: 300,
            background: 'linear-gradient(135deg, #0a1929 0%, #1a2942 100%)',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              MembershipSuite
            </Typography>
            <IconButton onClick={toggleMobileMenu} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />

          <Stack spacing={1}>
            {navItems.map((item) => (
              <Button
                key={item.name}
                component="a"
                href={item.link}
                onClick={toggleMobileMenu}
                sx={{
                  color: 'white',
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  opacity: activeSection === item.link.substring(1) ? 1 : 0.8,
                  fontWeight: activeSection === item.link.substring(1) ? 500 : 400,
                  borderLeft: activeSection === item.link.substring(1) ? '2px solid #64b5f6' : '2px solid transparent',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.05)',
                    opacity: 1,
                  },
                }}
              >
                {item.name}
              </Button>
            ))}
          </Stack>

          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                },
              }}
              component={Link}
              to="/login"
              onClick={toggleMobileMenu}
            >
              Log In
            </Button>
            <Button
              variant="contained"
              fullWidth
              sx={{
                background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #1976d2, #7b1fa2)',
                },
              }}
              component={Link}
              to="/register-tenant"
              onClick={toggleMobileMenu}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
};

const LandingPage = () => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const theme = useTheme();
  const [scrollPosition, setScrollPosition] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.pageYOffset);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'member') {
        navigate('/member/dashboard');
      } else if (user.role === 'superadmin') {
        navigate('/superadmin/dashboard');
      }
    }
  }, [user, navigate]);

  const FloatingActionButton = () => (
    <Zoom in={scrollPosition > 300}>
      <Fab
        component={motion.div}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        color="primary"
        size="medium"
        aria-label="scroll back to top"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
        }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <KeyboardArrowUp />
      </Fab>
    </Zoom>
  );

  return (
    <>
      <NavigationMenu />
      <Box sx={{ pt: 0 }}>
        <EnhancedHeroSection />
        <EnhancedFeaturesSection />
        <SecuritySection />
        <EnhancedAnalyticsDashboard />
        <TestimonialsSection />
        <FloatingActionButton />
      </Box>
    </>
  );
};

export default LandingPage;